import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Plus, FileText, Trash2, TrendingUp, Calendar } from "lucide-react";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Properties";
import { formatCurrency, formatDate } from "@/lib/format";
import StatusPill from "@/components/StatusPill";

const inputCls = "w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm";

const emptyForm = () => ({
  tenant_id: "", property_id: "", unit_label: "",
  start_date: "", end_date: "",
  monthly_rent: 0, commission_percent: 0, tax_percent: 0,
  increase_percent: 0, next_increase_date: "",
  deposit: 0, payment_day: 1, notes: "",
});

export default function Contracts() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [c, t, p] = await Promise.all([
      api.get("/contracts"),
      api.get("/tenants"),
      api.get("/properties"),
    ]);
    setItems(c.data);
    setTenants(t.data);
    setProperties(p.data);
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthly_rent: parseFloat(form.monthly_rent) || 0,
        commission_percent: parseFloat(form.commission_percent) || 0,
        tax_percent: parseFloat(form.tax_percent) || 0,
        increase_percent: parseFloat(form.increase_percent) || 0,
        deposit: parseFloat(form.deposit) || 0,
        payment_day: parseInt(form.payment_day) || 1,
        next_increase_date: form.next_increase_date || null,
      };
      await api.post("/contracts", payload);
      toast.success("Contract created — rent roll generated");
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch { toast.error("Could not save contract"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this contract and all its payments?")) return;
    await api.delete(`/contracts/${id}`);
    toast.success("Contract deleted");
    load();
  };

  const canCreate = tenants.length > 0 && properties.length > 0;

  return (
    <Layout
      title="Contracts"
      subtitle="Lease agreements, rent amounts and increase schedules"
      actions={
        <button
          onClick={() => setShowForm(true)} disabled={!canCreate}
          data-testid="add-contract-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> New contract
        </button>
      }
    >
      {items.length === 0 ? (
        <div className="card-flat p-10 text-center fade-up">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold">No contracts yet</div>
          <p className="text-sm text-slate-500 mt-1">
            {canCreate
              ? "Create your first lease — RoostHQ will auto-generate the monthly rent roll."
              : "Add at least one tenant and one property first."}
          </p>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto fade-up">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Tenant · Unit</th>
                <th className="text-left px-6 py-3 font-medium">Property</th>
                <th className="text-left px-6 py-3 font-medium">Term</th>
                <th className="text-right px-6 py-3 font-medium">Rent (gross)</th>
                <th className="text-left px-6 py-3 font-medium">Next increase</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/50" data-testid={`contract-row-${c.id}`}>
                  <td className="px-6 py-3.5">
                    <div className="font-medium">{c.tenant?.full_name || "—"}</div>
                    {c.unit_label && <div className="text-xs text-slate-500 mt-0.5">{c.unit_label}</div>}
                  </td>
                  <td className="px-6 py-3.5 text-slate-700">{c.property?.name || "—"}</td>
                  <td className="px-6 py-3.5 text-slate-700">
                    <div className="flex items-center gap-1.5 text-xs">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      {formatDate(c.start_date)} → {formatDate(c.end_date)}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">Due day {c.payment_day}</div>
                  </td>
                  <td className="px-6 py-3.5 text-right font-mono-plex font-medium">{formatCurrency(c.monthly_rent)}</td>
                  <td className="px-6 py-3.5 text-slate-700">
                    {c.next_increase_date ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <TrendingUp className="w-3 h-3 text-indigo-600" />
                        {formatDate(c.next_increase_date)} · +{c.increase_percent || 0}%
                      </span>
                    ) : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                  <td className="px-6 py-3.5">
                    <StatusPill status={c.status || "active"} />
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <button onClick={() => del(c.id)} data-testid={`contract-delete-${c.id}`} className="text-slate-400 hover:text-red-600 transition p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <Modal onClose={() => setShowForm(false)} title="New contract">
          <form onSubmit={save} className="space-y-4" data-testid="contract-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Tenant">
                <select required value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} data-testid="contract-tenant-select" className={inputCls}>
                  <option value="">Select tenant…</option>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </Field>
              <Field label="Property">
                <select required value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} data-testid="contract-property-select" className={inputCls}>
                  <option value="">Select property…</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Unit (label, optional)">
              <input value={form.unit_label} onChange={(e) => setForm({ ...form, unit_label: e.target.value })} data-testid="contract-unit-input" className={inputCls} placeholder="Unit 3B" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Start date">
                <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="contract-start-input" className={inputCls} />
              </Field>
              <Field label="End date">
                <input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="contract-end-input" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Monthly rent (R)">
                <input type="number" step="0.01" required value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} data-testid="contract-rent-input" className={inputCls} />
              </Field>
              <Field label="Commission %">
                <input type="number" step="0.1" value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} data-testid="contract-commission-input" className={inputCls} />
              </Field>
              <Field label="Tax %">
                <input type="number" step="0.1" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} data-testid="contract-tax-input" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Increase %">
                <input type="number" step="0.1" value={form.increase_percent} onChange={(e) => setForm({ ...form, increase_percent: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Next increase">
                <input type="date" value={form.next_increase_date} onChange={(e) => setForm({ ...form, next_increase_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Deposit (R)">
                <input type="number" step="0.01" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Rent due on day (of month)">
              <input type="number" min="1" max="28" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Notes">
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} h-20 py-2`} />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="contract-save-btn" className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">Save contract</button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
}
